import { SwipeableDrawer, Card, CardContent, Grid, Box, FormHelperText, Button, useMediaQuery, CardHeader } from "@mui/material";
import { useFormik } from "formik";
import { useRecordContext } from "ra-core";
import { FC, useCallback, useEffect, useRef } from "react";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { RecPatch } from "~/dataProvider/tools";
import { DataOwnerGroupResponseDto } from "~/gen/api/governance";


const validationSchema =
  Yup.object().shape({
    name: Yup.string().max(100).required('Name is required'),
    description: Yup.string().max(512),
  })


export interface EditDataOwnerGroupProps {
  open: boolean;
  setOpen: (x: boolean) => void;
}
  
const EditDataOwnerGroup: FC<EditDataOwnerGroupProps> = ({ open, setOpen }) => {
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  
  const record = useRecordContext<DataOwnerGroupResponseDto & RecPatch>()
    
  const save = useSimpleSaveAction()
  const initialValues = {
    submit: '',
    name: record?.name || '',
    description: record?.description || '',
  }

  const recordRef = useRef<boolean | null>(!!record)

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setOpen(false)
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    const payload = {
      ...values,
      ...(!!record && {ownerGroupId: record.ownerGroupId})
    }

    save(payload, options)
  }, [save, record, setOpen])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, resetForm, setValues } = formikCtrl
  const handleClose = useCallback(() => { 
    setOpen(false) 
    recordRef.current = null
    resetForm({
      values: { name: '', description: '', submit: '', }
    })
  }, [setOpen, resetForm])

  useEffect(() => {
    if (!recordRef.current && !!record) {
      recordRef.current = true
      setValues({
        name: record.name || '',
        description: record.description || '',
      } as any)
    }
  }, [record, setValues])
  
  return (
    <SwipeableDrawer
      anchor="right"
      onOpen={() => {}}
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardHeader title={!record ? "New Data Owner Group" : "Edit Data Owner Group"} />
          <CardContent>
            <Grid
              container
              spacing={4}
            >
              <Grid item xs={12} >
                <TextField label="Name*" name="name" />
              </Grid>
              <Grid item xs={12} >
                <TextField label="Description" name="description" multiline={true} />
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Card>

      </OdForm>

    </SwipeableDrawer>
  )
}

export default EditDataOwnerGroup;
